/**
 * Calendar Utility Functions
 *
 * Date math, grid builders, overlap layout, and recurrence label generation.
 */

import type { EventInstance, CalendarView, RecurrenceRule, CalendarSettings } from './types';
import { DAY_NAMES, MONTH_NAMES, FREQUENCY_LABELS, HOUR_HEIGHT_PX } from './types';

// =============================================================================
// DATE MATH
// =============================================================================

export function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function endOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

export function startOfWeek(date: Date, weekStartDay: number = 0): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = (day - weekStartDay + 7) % 7;
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function endOfWeek(date: Date, weekStartDay: number = 0): Date {
  const d = startOfWeek(date, weekStartDay);
  d.setDate(d.getDate() + 6);
  d.setHours(23, 59, 59, 999);
  return d;
}

export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
}

export function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

export function addMonths(date: Date, months: number): Date {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

export function addWeeks(date: Date, weeks: number): Date {
  return addDays(date, weeks * 7);
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function isToday(date: Date): boolean {
  return isSameDay(date, new Date());
}

export function isSameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

export function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

// =============================================================================
// VISIBLE RANGE COMPUTATION
// =============================================================================

export function getVisibleRange(
  currentDate: Date,
  view: CalendarView,
  settings: CalendarSettings,
): { start: Date; end: Date } {
  switch (view) {
    case 'month': {
      // Month grid starts at the beginning of the first visible week
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      const gridStart = startOfWeek(monthStart, settings.weekStartDay);
      const gridEnd = endOfWeek(monthEnd, settings.weekStartDay);
      return { start: gridStart, end: gridEnd };
    }
    case 'week': {
      const weekStart = startOfWeek(currentDate, settings.weekStartDay);
      const weekEnd = endOfWeek(currentDate, settings.weekStartDay);
      return { start: weekStart, end: weekEnd };
    }
    case 'day': {
      return { start: startOfDay(currentDate), end: endOfDay(currentDate) };
    }
    case 'agenda': {
      const agendaStart = startOfDay(currentDate);
      const agendaEnd = addDays(agendaStart, settings.agendaDaysAhead);
      return { start: agendaStart, end: agendaEnd };
    }
  }
}

// =============================================================================
// GRID BUILDERS
// =============================================================================

export interface MonthGridCell {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: EventInstance[];
}

export function buildMonthGrid(
  currentDate: Date,
  events: EventInstance[],
  weekStartDay: number = 0,
): MonthGridCell[][] {
  const monthStart = startOfMonth(currentDate);
  const gridStart = startOfWeek(monthStart, weekStartDay);

  const weeks: MonthGridCell[][] = [];
  let current = new Date(gridStart);

  for (let w = 0; w < 6; w++) {
    const week: MonthGridCell[] = [];
    for (let d = 0; d < 7; d++) {
      const cellDate = new Date(current);
      const dayEvents = events.filter((e) => {
        const eventStart = new Date(e.startAt);
        const eventEnd = new Date(e.endAt);
        return eventStart < endOfDay(cellDate) && eventEnd > startOfDay(cellDate);
      });

      week.push({
        date: cellDate,
        isCurrentMonth: isSameMonth(cellDate, currentDate),
        isToday: isToday(cellDate),
        events: dayEvents,
      });

      current = addDays(current, 1);
    }
    weeks.push(week);
  }

  return weeks;
}

export function getWeekDays(currentDate: Date, weekStartDay: number = 0): Date[] {
  const weekStart = startOfWeek(currentDate, weekStartDay);
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    days.push(addDays(weekStart, i));
  }
  return days;
}

// =============================================================================
// TIME GRID EVENT POSITIONING
// =============================================================================

export interface PositionedEvent {
  event: EventInstance;
  top: number;
  height: number;
  left: number;
  width: number;
  column: number;
  totalColumns: number;
}

export function positionTimedEvents(
  events: EventInstance[],
  dayDate: Date,
  hourHeightPx: number = HOUR_HEIGHT_PX,
): PositionedEvent[] {
  // Filter to timed events (not all-day) that overlap this day
  const timedEvents = events.filter((e) => {
    if (e.isAllDay) return false;
    const start = new Date(e.startAt);
    const end = new Date(e.endAt);
    return start < endOfDay(dayDate) && end > startOfDay(dayDate);
  });

  if (timedEvents.length === 0) return [];

  // Calculate pixel positions
  const positioned = timedEvents.map((event) => {
    const start = new Date(event.startAt);
    const end = new Date(event.endAt);

    // Clamp to day boundaries
    const dayStart = startOfDay(dayDate);
    const dayEnd = endOfDay(dayDate);
    const effectiveStart = start < dayStart ? dayStart : start;
    const effectiveEnd = end > dayEnd ? dayEnd : end;

    const startMinutes = effectiveStart.getHours() * 60 + effectiveStart.getMinutes();
    const endMinutes = effectiveEnd.getHours() * 60 + effectiveEnd.getMinutes();
    const durationMinutes = Math.max(endMinutes - startMinutes, 15); // Min 15 min height

    const top = (startMinutes / 60) * hourHeightPx;
    const height = (durationMinutes / 60) * hourHeightPx;

    return {
      event,
      top,
      height,
      left: 0,
      width: 100,
      column: 0,
      totalColumns: 1,
      startMinutes,
      endMinutes: startMinutes + durationMinutes,
    };
  });

  // Sort by start time, then by duration (longer events first)
  positioned.sort((a, b) => a.startMinutes - b.startMinutes || b.height - a.height);

  // Resolve overlaps using column layout
  const columns: { end: number }[] = [];

  for (const item of positioned) {
    // Find first available column
    let col = 0;
    for (col = 0; col < columns.length; col++) {
      if (columns[col].end <= item.startMinutes) break;
    }

    if (col === columns.length) {
      columns.push({ end: item.endMinutes });
    } else {
      columns[col].end = item.endMinutes;
    }

    item.column = col;
  }

  const totalColumns = columns.length;

  return positioned.map((item) => ({
    event: item.event,
    top: item.top,
    height: item.height,
    left: (item.column / totalColumns) * 100,
    width: 100 / totalColumns,
    column: item.column,
    totalColumns,
  }));
}

export function getAllDayEvents(events: EventInstance[], dayDate: Date): EventInstance[] {
  return events.filter((e) => {
    if (!e.isAllDay) return false;
    const start = new Date(e.startAt);
    const end = new Date(e.endAt);
    return start < endOfDay(dayDate) && end > startOfDay(dayDate);
  });
}

// =============================================================================
// FORMATTING
// =============================================================================

export function formatDateLabel(date: Date, view: CalendarView): string {
  switch (view) {
    case 'month':
      return `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
    case 'week': {
      const weekEnd = addDays(date, 6);
      if (date.getMonth() === weekEnd.getMonth()) {
        return `${MONTH_NAMES[date.getMonth()]} ${date.getDate()} – ${weekEnd.getDate()}, ${date.getFullYear()}`;
      }
      return `${MONTH_NAMES[date.getMonth()]} ${date.getDate()} – ${MONTH_NAMES[weekEnd.getMonth()]} ${weekEnd.getDate()}, ${date.getFullYear()}`;
    }
    case 'day':
      return `${DAY_NAMES[date.getDay()]}, ${MONTH_NAMES[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    case 'agenda':
      return `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
  }
}

export function formatTime(date: Date, timeFormat: '12h' | '24h' = '12h'): string {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  if (timeFormat === '24h') {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  const displayMinutes = minutes > 0 ? `:${String(minutes).padStart(2, '0')}` : '';
  return `${displayHour}${displayMinutes} ${ampm}`;
}

export function formatTimeRange(
  startAt: string,
  endAt: string,
  isAllDay: boolean,
  timeFormat: '12h' | '24h' = '12h',
): string {
  if (isAllDay) return 'All day';
  return `${formatTime(new Date(startAt), timeFormat)} – ${formatTime(new Date(endAt), timeFormat)}`;
}

export function formatDateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function formatAgendaDate(date: Date): string {
  const today = new Date();
  const tomorrow = addDays(today, 1);

  if (isSameDay(date, today)) return 'Today';
  if (isSameDay(date, tomorrow)) return 'Tomorrow';

  return `${DAY_NAMES[date.getDay()]}, ${MONTH_NAMES[date.getMonth()]} ${date.getDate()}`;
}

// =============================================================================
// RECURRENCE LABEL
// =============================================================================

export function buildRecurrenceLabel(rule: RecurrenceRule): string {
  const freqLabel = FREQUENCY_LABELS[rule.frequency] || rule.frequency;
  let label = rule.interval > 1 ? `Every ${rule.interval} ${freqLabel}s` : `Every ${freqLabel}`;

  if (rule.frequency === 'weekly' && rule.byDay && rule.byDay.length > 0) {
    label += ` on ${rule.byDay.join(', ')}`;
  }

  if (rule.endType === 'count' && rule.count) {
    label += `, ${rule.count} times`;
  } else if (rule.endType === 'until' && rule.until) {
    const untilDate = new Date(rule.until);
    label += `, until ${MONTH_NAMES[untilDate.getMonth()]} ${untilDate.getDate()}, ${untilDate.getFullYear()}`;
  }

  return label;
}

export function parseRruleToRule(rruleStr: string | null): RecurrenceRule | null {
  if (!rruleStr) return null;

  const clean = rruleStr.replace(/^RRULE:/, '');
  const parts = new Map<string, string>();
  for (const part of clean.split(';')) {
    const [key, val] = part.split('=');
    if (key && val) parts.set(key.toUpperCase(), val);
  }

  const freqMap: Record<string, RecurrenceRule['frequency']> = {
    DAILY: 'daily',
    WEEKLY: 'weekly',
    MONTHLY: 'monthly',
    YEARLY: 'yearly',
  };

  const frequency = freqMap[parts.get('FREQ') || ''] || 'weekly';
  const interval = parseInt(parts.get('INTERVAL') || '1', 10);

  const byDay = parts.get('BYDAY')?.split(',');

  let endType: RecurrenceRule['endType'] = 'never';
  let count: number | undefined;
  let until: string | undefined;

  if (parts.has('COUNT')) {
    endType = 'count';
    count = parseInt(parts.get('COUNT')!, 10);
  } else if (parts.has('UNTIL')) {
    endType = 'until';
    const untilStr = parts.get('UNTIL')!;
    // Parse YYYYMMDDTHHMMSSZ format
    const match = untilStr.match(/^(\d{4})(\d{2})(\d{2})/);
    if (match) {
      until = `${match[1]}-${match[2]}-${match[3]}`;
    }
  }

  return { frequency, interval, byDay, endType, count, until };
}

export function buildRruleString(rule: RecurrenceRule): string {
  const freqMap: Record<string, string> = {
    daily: 'DAILY',
    weekly: 'WEEKLY',
    monthly: 'MONTHLY',
    yearly: 'YEARLY',
  };

  let rrule = `RRULE:FREQ=${freqMap[rule.frequency]}`;

  if (rule.interval > 1) {
    rrule += `;INTERVAL=${rule.interval}`;
  }

  if (rule.frequency === 'weekly' && rule.byDay && rule.byDay.length > 0) {
    rrule += `;BYDAY=${rule.byDay.join(',')}`;
  }

  if (rule.endType === 'count' && rule.count) {
    rrule += `;COUNT=${rule.count}`;
  } else if (rule.endType === 'until' && rule.until) {
    const date = new Date(rule.until);
    const untilStr = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}T235959Z`;
    rrule += `;UNTIL=${untilStr}`;
  }

  return rrule;
}

// =============================================================================
// NAVIGATION
// =============================================================================

export function navigateDate(
  currentDate: Date,
  view: CalendarView,
  direction: 'prev' | 'next',
): Date {
  const delta = direction === 'next' ? 1 : -1;

  switch (view) {
    case 'month':
      return addMonths(currentDate, delta);
    case 'week':
      return addWeeks(currentDate, delta);
    case 'day':
      return addDays(currentDate, delta);
    case 'agenda':
      return addDays(currentDate, delta * 7);
  }
}

// =============================================================================
// VIDEO CALL DETECTION
// =============================================================================

export type VideoProvider = 'zoom' | 'meet' | 'teams' | 'webex';

export interface VideoCallInfo {
  provider: VideoProvider;
  url: string;
}

const VIDEO_PATTERNS: { provider: VideoProvider; regex: RegExp }[] = [
  { provider: 'zoom', regex: /https?:\/\/[\w.-]*zoom\.us\/j\/\S+/ },
  { provider: 'meet', regex: /https?:\/\/meet\.google\.com\/[\w-]+/ },
  { provider: 'teams', regex: /https?:\/\/teams\.microsoft\.com\/l\/meetup-join\/\S+/ },
  { provider: 'webex', regex: /https?:\/\/[\w.-]*webex\.com\/meet\/\S+/ },
];

export function detectVideoLink(event: {
  url?: string;
  location?: string;
  description?: string;
}): VideoCallInfo | null {
  const fields = [event.url, event.location, event.description];
  for (const field of fields) {
    if (!field) continue;
    for (const { provider, regex } of VIDEO_PATTERNS) {
      const match = field.match(regex);
      if (match) {
        return { provider, url: match[0] };
      }
    }
  }
  return null;
}

export const VIDEO_PROVIDER_LABELS: Record<VideoProvider, string> = {
  zoom: 'Join Zoom',
  meet: 'Join Meet',
  teams: 'Join Teams',
  webex: 'Join Webex',
};

// =============================================================================
// CONFLICT DETECTION
// =============================================================================

/**
 * Find events that overlap with a proposed time range.
 * Ignores all-day events and optionally excludes the event being edited.
 */
export function findConflicts(
  startAt: string,
  endAt: string,
  events: EventInstance[],
  excludeEventId?: string,
): EventInstance[] {
  const start = new Date(startAt);
  const end = new Date(endAt);

  return events.filter((e) => {
    if (e.isAllDay) return false;
    if (excludeEventId && e.eventId === excludeEventId) return false;
    const eStart = new Date(e.startAt);
    const eEnd = new Date(e.endAt);
    return eStart < end && eEnd > start;
  });
}

/**
 * Given a list of timed events for a single day, return the set of event IDs
 * that overlap with at least one other event.
 */
export function findOverlappingEventIds(events: EventInstance[]): Set<string> {
  const timed = events.filter((e) => !e.isAllDay);
  const overlapping = new Set<string>();

  for (let i = 0; i < timed.length; i++) {
    const aStart = new Date(timed[i].startAt);
    const aEnd = new Date(timed[i].endAt);
    for (let j = i + 1; j < timed.length; j++) {
      const bStart = new Date(timed[j].startAt);
      const bEnd = new Date(timed[j].endAt);
      if (aStart < bEnd && bStart < aEnd) {
        overlapping.add(timed[i].eventId);
        overlapping.add(timed[j].eventId);
      }
    }
  }

  return overlapping;
}

// =============================================================================
// BREAK GAP DETECTION
// =============================================================================

export interface BreakWarning {
  eventBefore: EventInstance;
  eventAfter: EventInstance;
  gapMinutes: number;
}

/**
 * Find pairs of adjacent (non-overlapping) events on a given day where the gap
 * between them is positive but less than `minBreakMinutes`.
 */
export function findInsufficientBreaks(
  events: EventInstance[],
  day: Date,
  minBreakMinutes: number,
): BreakWarning[] {
  if (minBreakMinutes <= 0) return [];

  const dayStart = startOfDay(day);
  const dayEnd = endOfDay(day);

  const timed = events
    .filter((e) => {
      if (e.isAllDay) return false;
      const s = new Date(e.startAt);
      const end = new Date(e.endAt);
      return s < dayEnd && end > dayStart;
    })
    .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());

  const warnings: BreakWarning[] = [];

  for (let i = 0; i < timed.length - 1; i++) {
    const endA = new Date(timed[i].endAt);
    const startB = new Date(timed[i + 1].startAt);
    const gapMs = startB.getTime() - endA.getTime();

    if (gapMs > 0 && gapMs < minBreakMinutes * 60 * 1000) {
      warnings.push({
        eventBefore: timed[i],
        eventAfter: timed[i + 1],
        gapMinutes: Math.round(gapMs / 60000),
      });
    }
  }

  return warnings;
}

// =============================================================================
// EVENT COLOR
// =============================================================================

export function getEventColor(
  event: EventInstance,
  colorPriority: 'event' | 'calendar' = 'event',
): string {
  if (colorPriority === 'calendar') {
    return event.calendarColor || event.color || '#3b82f6';
  }
  return event.color || event.calendarColor || '#3b82f6';
}
